/**
 * Weiche Wohnen ↔ Gewerbe für Sicherheit, Brandschutz, Meldeanlagen und
 * Sonderausstattung (Fachabstimmung 31.08.2026: *„da muss noch ein bisschen
 * die Schere zwischen Wohnen und Gewerbe"*, z. B. *„Rolltreppen in keinem
 * Mehrfamilienhaus"*, RWA und Notstrom nicht bei Wohnen).
 *
 * Regeln:
 * - Nur beim reinen Wohngebäude (`Wohnen (100%)`) wird ausgeblendet.
 *   Gewerbe, Mischnutzung und eine noch offene Kategorie zeigen alles.
 * - Das Wohnhochhaus behält die Hochhaus-Technik (Steigleitungen,
 *   Wandhydranten, Sprinkler, RWA, Notstrom) — die schreibt dort die
 *   Hochhausrichtlinie vor.
 * - Was schon beantwortet ist, bleibt immer sichtbar: Bestandsangaben dürfen
 *   durch die Weiche nicht verschwinden.
 *
 * Die Zuordnung ist ein fachlicher Vorschlag (21.10.2026) und mit DC auf DEV
 * abzustimmen — sie steht deshalb an genau dieser einen Stelle.
 */

export type Nutzungsart = "wohnen" | "gewerbe" | "misch" | "offen";

export const nutzungsartVon = (answers: Record<string, unknown>): Nutzungsart => {
  switch (answers.kategorie) {
    case "Wohnen (100%)":
      return "wohnen";
    case "Gewerbe (100%)":
      return "gewerbe";
    case "Mischnutzung (Wohnen u. Gewerbe)":
      return "misch";
    default:
      return "offen";
  }
};

/** Anlagen (Schritt „Sicherheit", Schlüssel aus `anlagenKatalog.ts`), die es nur bei Gewerbe gibt. */
export const NUR_GEWERBE_ANLAGEN: ReadonlySet<string> = new Set([
  "rwa",
  "sprinkler",
  "wandhydranten",
  "funkenloeschanlagen",
  "gasfeuerloeschanlagen",
  "stickstoffanlage",
  "kleinloeschanlagen",
  "schaumloeschanlagen",
  "spruehwasserloeschanlagen",
  "entrauchungsanlagen",
  "foerderanlagenabschluesse",
  "pulverloeschanlagen",
  "steigleitungen",
  "batterieladestation",
  "kompressorenLueftung",
  "netzersatzanlage",
  "netzersatzanlageDiesel",
]);

/** Hochhaus-Technik, die auch das Wohnhochhaus behält. */
const HOCHHAUS_ANLAGEN: ReadonlySet<string> = new Set([
  "rwa",
  "sprinkler",
  "wandhydranten",
  "steigleitungen",
  "netzersatzanlage",
  "netzersatzanlageDiesel",
]);

const istWohnhochhaus = (answers: Record<string, unknown>) => answers.typ === "Wohnhochhaus";

const hatInhalt = (v: unknown): boolean => {
  if (v == null) return false;
  if (typeof v === "string") return v.trim() !== "";
  if (typeof v === "object") return Object.keys(v as object).length > 0;
  return true;
};

/** Steht eine Anlage (Katalog-Schlüssel) für dieses Gebäude zur Auswahl? */
export const anlageSichtbar = (key: string, answers: Record<string, unknown>): boolean => {
  if (nutzungsartVon(answers) !== "wohnen") return true;
  if (!NUR_GEWERBE_ANLAGEN.has(key)) return true;
  if (istWohnhochhaus(answers) && HOCHHAUS_ANLAGEN.has(key)) return true;
  return hatInhalt(answers[`sicherheit_${key}`]);
};

/** Die Frage „Notstromversorgung vorhanden?" — wie die Netzersatzanlage nur bei Gewerbe. */
export const notstromSichtbar = (answers: Record<string, unknown>): boolean =>
  nutzungsartVon(answers) !== "wohnen" || istWohnhochhaus(answers) || hatInhalt(answers.notstrom);

/**
 * Ein Eintrag der Sonderausstattung/Wellness (`nutzung: "gewerbe"` in
 * `enums.ts`). `werte` ist das gespeicherte Katalog-Objekt, z. B.
 * `answers.sonderausstattungen`.
 */
export const ausstattungSichtbar = (
  eintrag: { key: string; nutzung?: string },
  answers: Record<string, unknown>,
  werte: Record<string, unknown> | undefined,
): boolean => {
  if (eintrag.nutzung !== "gewerbe") return true;
  if (nutzungsartVon(answers) !== "wohnen") return true;
  return hatInhalt(werte?.[eintrag.key]);
};
